import {
  StatusBar,
  MultiSelect,
  SearchField,
  SnackbarStatus,
  Typography,
  VenueIcon,
} from '@weareopenr/component-library';
import { FEATURE_FLAGS } from 'src/constants';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useForm } from 'react-hook-form';
import { IPriceBandFields } from 'src/types/priceRules';
import styles from 'src/components/PriceRuleFlowShared/PriceRule.module.scss';
import { useMemo, useState } from 'react';
import { IPriceBandWithVenues } from 'src/types/priceBand.types';
import PriceRuleAdjustmentOptions from '../PriceRuleAdjustmentOptions';
import CollapsibleSection from '../CollapsibleSection';
import { AdjustmentType, RoundingType } from '@weareopenr/types';
import {
  buildCalculationSubtitle,
  buildRoundingSubtitle,
  getTitles,
  TPriceRuleTitleStep,
} from 'src/utils/priceRules/confirmationStrings';
import { RoundingRuleOptions } from '../RoundingRuleOptions';
import { IGetVenuesResponseData } from 'src/api/getVenues/types';
import { createPriceRules } from 'src/api/createPriceRules';
import { createPriceBand } from 'src/api/createPriceBand/createPriceBand';
import {
  createPriceRuleAdjustment,
  createPriceRuleRounding,
  defaultAdjustmentValues,
  defaultRoundingValues,
} from 'src/utils/priceRules/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthWrapper } from '@weareopenr/authkit';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { CREATE_PRICE_BAND_NOTIFICATION, UPDATE_PRICE_BAND_NOTIFICATION } from './constants';
import PriceBandName from '../PriceRuleFlowShared/PriceBandName';
import { updatePriceRule } from 'src/api/updatePriceRule/updatePriceRule';
import { updatePriceBand } from 'src/api/updatePriceBand/updatePriceBand';
import PriceRuleContinueButtons from '../PriceRuleFlowShared/PriceRuleContinueButtons';
import { TPriceBandAndRules } from '../PriceSchemeBandsRulesTab/types';
import SelectableListWithControls from '../SelectableListWithControls/SelectableListWithControls';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import {
  venueCategoriesToSelectOptions,
  venuesInSelectedCategories,
} from 'src/utils/venueCategories/mappedVenueCategories';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';

const RULES_ONLY_TOTAL_STEPS = 3;
const FULL_FLOW_TOTAL_STEPS = 5;

type VenueWithBandName = IGetVenuesResponseData & { price_band_name?: string };

interface IPriceBandDetailsForm {
  defaultStep?: number;
  priceSchemeId: string;
  priceSchemeName: string;
  priceBandToEdit?: TPriceBandAndRules;
  closeModal: () => void;
  venues: VenueWithBandName[];
  priceBands: IPriceBandWithVenues[];
}

export enum PriceBandRuleSteps {
  Name = 0,
  Venues = 1,
  Adjustment = 2,
  Rounding = 3,
  Confirm = 4,
}

interface IPriceBandFormFields extends IPriceBandFields {
  filteredText?: string;
  filteredCategoryIds?: string[];
}

const isContinueButtonDisabled = (
  step: PriceBandRuleSteps,
  { name, rounding, adjustment }: IPriceBandFormFields,
): boolean => {
  switch (step) {
    case PriceBandRuleSteps.Name:
      return name.trim().length === 0;
    case PriceBandRuleSteps.Adjustment: {
      const adjustmentEnabled = adjustment.type !== AdjustmentType.NONE;
      const noValueExists = Number.isNaN(adjustment.fixed) && Number.isNaN(adjustment.percentage);

      return adjustmentEnabled && noValueExists;
    }
    case PriceBandRuleSteps.Rounding: {
      const roundingEnabled = rounding.type !== RoundingType.NONE && typeof rounding.method !== 'undefined';
      const noValueExists = typeof rounding.value === 'undefined' || Number.isNaN(rounding.value);
      return roundingEnabled && noValueExists;
    }
    default:
      return false;
  }
};

const createSteps: TPriceRuleTitleStep[] = [
  {
    title: 'Price Band Name',
    subtitle: 'Price bands allow you to set different prices for your products and allocate them to venues.',
  },
  {
    title: 'Venue Allocation',
    subtitle:
      'Assign venues to a price band, and when a menu is published, the appropriate banded pricing is automatically applied for each venue.',
  },
  {
    title: 'Price Band Calculation',
    subtitle: 'Automate pricing adjustments for this price band based on default product values.',
  },
  {
    title: 'Price Band Rounding',
    subtitle: 'Create a rounding rule for all automated price changes within this price band.',
  },
];

const venueToOption = (venue: VenueWithBandName) => ({
  id: venue.venue_id,
  label: venue.name,
  subtitle: venue.price_band_name || 'Default',
});

const PriceBandRuleModalContent = ({
  priceBandToEdit,
  closeModal,
  priceSchemeId,
  priceSchemeName,
  venues,
  defaultStep,
  priceBands,
}: IPriceBandDetailsForm) => {
  const queryClient = useQueryClient();
  const { featureFlags } = useAuthWrapper();
  const newPricingRulesFlow = featureFlags.includes(FEATURE_FLAGS.PRICING_RULES);

  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [step, setStep] = useState<number>(defaultStep ?? PriceBandRuleSteps.Name);

  const priceBandNames = priceBands.map(({ name }) => name.toLocaleLowerCase());
  const { nestedCategories: nestedVenueCategories = [], data: venueCategories } = useVenueCategories();

  const checkboxOptions = useMemo(() => venues.map(venueToOption), [venues]);

  const defaultName = priceBandToEdit?.name ?? '';
  const defaultVenues = priceBandToEdit?.venues.map(({ venueId }) => venueId) ?? [];
  const defaultRounding = defaultRoundingValues(priceBandToEdit?.rounding);
  const defaultAdjustment = defaultAdjustmentValues(priceBandToEdit?.adjustment);

  const formFields = useForm<IPriceBandFormFields>({
    mode: 'onChange',
    defaultValues: {
      name: defaultName,
      entities: defaultVenues,
      rounding: defaultRounding,
      adjustment: defaultAdjustment,
      filteredText: '',
      filteredCategoryIds: [],
    },
  });

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = formFields;

  const decrementStep = () => setStep((s) => s - 1);
  const incrementStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStep((s) => s + 1);
  };

  // Handles creating brand new band + rule (full 5-step flow only)
  const onCreate = async ({ name, entities, rounding, adjustment }: IPriceBandFormFields) => {
    const { price_band_id } = await createPriceBand({ name, venues: entities }, priceSchemeId);
    await createPriceRules(
      [
        {
          adjustment: createPriceRuleAdjustment(adjustment),
          rounding: createPriceRuleRounding(rounding),
          price_band_id,
        },
      ],
      priceSchemeId,
    );
  };

  const onUpdate = async (fields: IPriceBandFormFields, step: PriceBandRuleSteps) => {
    if (step === PriceBandRuleSteps.Name || step === PriceBandRuleSteps.Venues) {
      await updatePriceBand(priceBandToEdit.priceBandId, priceSchemeId, { name: fields.name, venues: fields.entities });
    } else {
      const ruleData = {
        price_band_id: priceBandToEdit.priceBandId,
        adjustment: createPriceRuleAdjustment(fields.adjustment),
        rounding: createPriceRuleRounding(fields.rounding),
      };

      if (priceBandToEdit.price_rule_id) {
        await updatePriceRule([{ price_rule_id: priceBandToEdit.price_rule_id, ...ruleData }], priceSchemeId);
      } else {
        await createPriceRules([ruleData], priceSchemeId);
      }
    }
  };

  const isEditMode = !!priceBandToEdit && (!newPricingRulesFlow || !!priceBandToEdit.price_rule_id);
  const isVenueEditOnly = defaultStep === PriceBandRuleSteps.Venues;
  const isSingleFieldEdit = isEditMode || (newPricingRulesFlow && isVenueEditOnly);

  const onSubmit = async ({ entities, adjustment, name, rounding }: IPriceBandFormFields) => {
    clearError();
    const fields = { entities, adjustment, name, rounding };

    try {
      if (newPricingRulesFlow) {
        const ruleData = {
          price_band_id: priceBandToEdit.priceBandId,
          adjustment: createPriceRuleAdjustment(fields.adjustment),
          rounding: createPriceRuleRounding(fields.rounding),
        };

        if (isEditMode) {
          if (isVenueEditOnly) {
            await updatePriceBand(priceBandToEdit.priceBandId, priceSchemeId, {
              venues: fields.entities,
            });
          } else {
            await updatePriceRule([{ price_rule_id: priceBandToEdit.price_rule_id, ...ruleData }], priceSchemeId);
          }
        } else {
          await createPriceRules([ruleData], priceSchemeId);
        }

        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: UPDATE_PRICE_BAND_NOTIFICATION.SUCCESS,
        });
      } else {
        // Old flow
        if (isEditMode) {
          await onUpdate(fields, defaultStep);
        } else {
          await onCreate(fields);
        }

        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: isEditMode ? UPDATE_PRICE_BAND_NOTIFICATION.SUCCESS : CREATE_PRICE_BAND_NOTIFICATION.SUCCESS,
        });
      }

      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.at(1) === QueryKeys.priceBands || queryKey.at(1) === QueryKeys.priceRules,
      });
      closeModal();
    } catch (error) {
      const fallbackMsg = isEditMode ? UPDATE_PRICE_BAND_NOTIFICATION.ERROR : CREATE_PRICE_BAND_NOTIFICATION.ERROR;
      const message = error instanceof Error ? error.message : fallbackMsg;

      showNotification({
        status: SnackbarStatus.ERROR,
        message,
      });
    }
  };

  const { rounding, adjustment, entities, name, filteredText, filteredCategoryIds } = watch();
  const searchQuery = useDebounce(filteredText);

  const roundingSubtitle = useMemo(() => buildRoundingSubtitle(rounding), [step, rounding]);
  const calculationSubtitle = useMemo(() => buildCalculationSubtitle(adjustment), [step, adjustment]);

  const [title, subtitle] = getTitles(step, createSteps);
  const selectedVenues = venues.filter(({ venue_id }) => entities.includes(venue_id));
  const continueDisabled = isContinueButtonDisabled(step, { entities, name, rounding, adjustment });

  const isRulesOnlyFlow =
    newPricingRulesFlow && (defaultStep ?? PriceBandRuleSteps.Name) >= PriceBandRuleSteps.Adjustment;
  const totalSteps = isRulesOnlyFlow ? RULES_ONLY_TOTAL_STEPS : FULL_FLOW_TOTAL_STEPS;
  const startingStep = isRulesOnlyFlow ? step - PriceBandRuleSteps.Adjustment : step;

  const showConfirmBtn = step === PriceBandRuleSteps.Confirm || isSingleFieldEdit;
  const showCancelBtn = step === (defaultStep ?? PriceBandRuleSteps.Name) || isSingleFieldEdit;

  const mappedVenueCategories = useMemo(
    () => venueCategoriesToSelectOptions(nestedVenueCategories),
    [nestedVenueCategories],
  );

  const venuesInSelectedCats = venuesInSelectedCategories(filteredCategoryIds, venueCategories?.categories);

  const filteredOptions = useMemo(
    () =>
      checkboxOptions.filter(({ label, id }) => {
        // Check if venue name matches search query
        const matchesSearch = searchQuery ? label.toLowerCase().includes(searchQuery.toLowerCase()) : true;

        // If no categories selected, show all venues that match search
        if (filteredCategoryIds.length === 0) {
          return matchesSearch;
        }

        // If categories are selected, show only venues in those categories that also match search
        return venuesInSelectedCats.includes(id) && matchesSearch;
      }),
    [checkboxOptions, searchQuery, filteredCategoryIds, venuesInSelectedCats],
  );

  const selectedCheckboxOptions = useMemo(
    () => checkboxOptions.filter(({ id }) => entities.includes(id)),
    [checkboxOptions, entities],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="price-band-form" aria-label="price-band-form">
      {!isSingleFieldEdit && (
        <Typography className={styles['steps']}>{`Step ${startingStep + 1} of ${totalSteps}`}</Typography>
      )}
      <Typography className={styles['title']} size="xxlarge" weight="semi-bold">
        {title}
      </Typography>
      <Typography className={styles['subtitle']}>{subtitle}</Typography>
      {step === PriceBandRuleSteps.Venues && (
        <Typography className={styles['price-scheme-headline']}>
          Price Scheme - <span className={styles['price-scheme-subtitle']}>{priceSchemeName}</span>
        </Typography>
      )}

      {step === PriceBandRuleSteps.Name && (
        <PriceBandName
          priceBandNames={priceBandNames}
          isCreate={Boolean(priceBandToEdit?.price_rule_id)}
          error={errors.name?.message}
          register={register}
        />
      )}

      {step === PriceBandRuleSteps.Venues && (
        <SelectableListWithControls
          options={filteredOptions}
          selectedOptionsIds={entities}
          onOptionChange={(selectedOptions) => setValue('entities', selectedOptions)}
          collapsible={{ selectedOptionsData: selectedCheckboxOptions }}
          listDetails={{ title: 'Venues:' }}
        >
          <SearchField
            isDisabled={false}
            placeholder="Search"
            className={styles['filter-searchfield']}
            onChange={({ target }) => setValue('filteredText', target.value)}
          />
          <MultiSelect
            name="venue-categories"
            Icon={VenueIcon}
            label="Categories"
            selected={filteredCategoryIds}
            options={mappedVenueCategories}
            className={styles['filter-multiselect']}
            onChange={(ids) => setValue('filteredCategoryIds', ids)}
            disableRecursiveParentSelection
          />
        </SelectableListWithControls>
      )}

      {step === PriceBandRuleSteps.Adjustment && (
        <PriceRuleAdjustmentOptions register={register as any} control={control as any} entity="Rule" />
      )}

      {step === PriceBandRuleSteps.Rounding && (
        <RoundingRuleOptions register={register as any} control={control as any} errors={errors} />
      )}

      {step === PriceBandRuleSteps.Confirm && (
        <>
          <Typography className={styles['title']} size="xxlarge" weight="semi-bold">
            Summary for <b>{name}</b>
          </Typography>
          <div className={styles['collapsible-container']}>
            <CollapsibleSection number="1" title="Venue Allocation" subtitle={`${entities?.length} venue(s) selected`}>
              {selectedVenues.length > 0 && (
                <ul>
                  {selectedVenues.map((v) => (
                    <li key={v.venue_id}>{v.name}</li>
                  ))}
                </ul>
              )}
            </CollapsibleSection>
            <CollapsibleSection number="2" title="Calculation" subtitle={calculationSubtitle} />
            <CollapsibleSection number="3" title="Rounding" subtitle={roundingSubtitle} />
          </div>
        </>
      )}

      <PriceRuleContinueButtons
        showCancelBtn={showCancelBtn}
        closeModal={closeModal}
        decrementStep={decrementStep}
        showConfirmBtn={showConfirmBtn}
        isSubmitting={isSubmitting}
        continueDisabled={continueDisabled}
        incrementStep={incrementStep}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export default PriceBandRuleModalContent;
